import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const toUser = (d) => ({
  username: d.username,
  role: d.role,
  grc_role: d.grc_role,
  role_label: d.role_label,
  tenant_id: d.tenant_id,
  tenant_name: d.tenant_name,
  is_demo_tenant: !!d.is_demo_tenant,
  permissions: d.permissions || {},
  memberships: d.memberships || [],
});

const TIER1_MODULES = new Set(["tenant_settings", "users", "audit_log"]);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("grc_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [token, setToken] = useState(() => localStorage.getItem("grc_token"));
  const [supportStatus, setSupportStatus] = useState(null);

  useEffect(() => {
    if (token) localStorage.setItem("grc_token", token);
    else localStorage.removeItem("grc_token");
  }, [token]);

  useEffect(() => {
    if (user) localStorage.setItem("grc_user", JSON.stringify(user));
    else localStorage.removeItem("grc_user");
  }, [user]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(toUser(data));
    } catch (e) {
      if (e?.response?.status === 401) logout();
    }
  }, [logout]);

  useEffect(() => {
    if (token) refreshMe();
  }, [token, refreshMe]);

  const refreshSupportStatus = useCallback(async () => {
    if (!token) {
      setSupportStatus(null);
      return;
    }
    try {
      const { data } = await api.get("/platform/support-mode/status");
      setSupportStatus(data);
    } catch (e) {
      setSupportStatus(null);
    }
  }, [token]);

  useEffect(() => {
    if (user?.grc_role === "platform_admin") refreshSupportStatus();
    else setSupportStatus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.tenant_id, user?.grc_role]);

  const login = async (username, password, remember = false) => {
    const { data } = await api.post("/auth/login", { username, password, remember });
    localStorage.setItem("grc_token", data.access_token);
    setToken(data.access_token);
    setUser(toUser(data));
    return data;
  };

  const switchTenant = async (tenantId) => {
    const { data } = await api.post("/auth/switch-tenant", { tenant_id: tenantId });
    localStorage.setItem("grc_token", data.access_token);
    setToken(data.access_token);
    setUser(toUser(data));
    return data;
  };

  const can = useCallback(
    (module, action = "view") => {
      if (!user) return false;
      if (user.grc_role === "platform_admin") {
        if (TIER1_MODULES.has(module)) return true;
        const g = supportStatus?.active;
        if (!g) return false;
        return action === "view" || !!g.allow_write;
      }
      return !!user.permissions?.[module]?.[action];
    },
    [user, supportStatus]
  );

  return (
    <AuthContext.Provider
      value={{ user, token, login, logout, switchTenant, refreshMe, can, isAuthed: !!token, supportStatus, refreshSupportStatus }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
